import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchFraudOverview,
  fetchFraudFlaggedMembers,
  fetchFraudSignalFeed,
  fetchFraudDeviceClusters,
  fetchFraudIpClusters,
} from '../../lib/adminApi';

export const SIGNAL_LABELS: Record<string, string> = {
  device_collision: 'Device Collision',
  ip_collision: 'IP Collision',
  email_pattern: 'Email Pattern',
  vpn_proxy_detected: 'VPN/Proxy Detected',
  geo_mismatch: 'Location Mismatch',
  profile_implausible: 'Implausible Profile',
  profile_velocity: 'Profile Velocity',
  quality_fail_pattern: 'Quality Fail Pattern',
  excessive_screenout: 'Excessive Screenouts',
  completion_velocity: 'Completion Velocity',
  writing_low_score: 'Low Writing Score',
  writing_translation: 'Translation Detected',
  browser_timezone_mismatch: 'Timezone Mismatch',
  webrtc_leak: 'WebRTC IP Leak',
  typing_anomaly: 'Typing Anomaly',
  identity_dob_mismatch: 'ID/DOB Mismatch',
  manual_review: 'Manual Review',
};

const TABS = ['Overview', 'Flagged Members', 'Signal Feed', 'Device Clusters', 'IP Clusters', 'Detection Effectiveness'] as const;
type Tab = (typeof TABS)[number];

const TIER_BADGE: Record<string, string> = {
  clean: 'bg-emerald-900/50 text-emerald-400',
  watch: 'bg-amber-900/50 text-amber-400',
  shadow: 'bg-orange-900/50 text-orange-400',
  banned: 'bg-red-900/50 text-red-400',
};

const SEVERITY_BADGE: Record<string, string> = {
  low: 'bg-slate-700 text-slate-300',
  medium: 'bg-yellow-900/50 text-yellow-400',
  high: 'bg-orange-900/50 text-orange-400',
  critical: 'bg-red-900/50 text-red-400',
};

const CONFIDENCE_BADGE: Record<string, string> = {
  low: 'bg-slate-700 text-slate-300',
  medium: 'bg-blue-900/50 text-blue-400',
  high: 'bg-orange-900/50 text-orange-400',
  critical: 'bg-red-900/50 text-red-400',
};

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const diffMs = Date.now() - new Date(iso).getTime();
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function TierBadge({ tier }: { tier: string }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TIER_BADGE[tier] ?? 'bg-slate-700 text-slate-300'}`}>
      {tier}
    </span>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_BADGE[severity] ?? 'bg-slate-700 text-slate-300'}`}>
      {severity}
    </span>
  );
}

function ConfidenceBadge({ confidence }: { confidence: string }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CONFIDENCE_BADGE[confidence] ?? 'bg-slate-700 text-slate-300'}`}>
      {confidence}
    </span>
  );
}

// ── Tab: Overview ─────────────────────────────────────────────────────────

function OverviewTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'fraud', 'overview'],
    queryFn: fetchFraudOverview,
  });

  if (isLoading) return <p className="text-slate-400 text-sm">Loading overview...</p>;
  if (!data) return <p className="text-slate-400 text-sm">No data available.</p>;

  const tierCounts = data.tierCounts ?? {};
  const total = Object.values(tierCounts).reduce((s: number, v) => s + (v as number), 0) || 1;
  const tiers: { key: string; label: string }[] = [
    { key: 'clean', label: 'Clean' },
    { key: 'watch', label: 'Watch' },
    { key: 'shadow', label: 'Shadow' },
    { key: 'banned', label: 'Banned' },
  ];

  const signalActivity = data.signalActivity ?? {};
  const topSignals: { type: string; count: number }[] = data.topSignals ?? [];
  const effectiveness = data.effectiveness ?? {};

  return (
    <div className="space-y-6">
      {/* Tier stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tiers.map(({ key, label }) => {
          const count = tierCounts[key] ?? 0;
          const pct = ((count / total) * 100).toFixed(1);
          return (
            <div key={key} className="bg-slate-800 rounded-xl p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
              <p className="text-2xl font-bold text-white mt-1 tabular-nums">{count.toLocaleString()}</p>
              <p className="text-xs text-slate-500 mt-0.5">{pct}% of members</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Signal Activity */}
        <div className="bg-slate-800 rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-bold text-white">Signal Activity</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">Signals today</span>
              <span className="text-sm font-medium text-white tabular-nums">{signalActivity.today ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">Signals this week</span>
              <span className="text-sm font-medium text-white tabular-nums">{signalActivity.thisWeek ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">Quality fail rate (30d)</span>
              <span className="text-sm font-medium text-white tabular-nums">
                {signalActivity.qualityFailRate30d != null ? `${(signalActivity.qualityFailRate30d * 100).toFixed(1)}%` : '--'}
              </span>
            </div>
          </div>
        </div>

        {/* Top Signal Types */}
        <div className="bg-slate-800 rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-bold text-white">Top Signal Types (30 days)</h3>
          {topSignals.length === 0 ? (
            <p className="text-xs text-slate-500">No signals recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {topSignals.map((s: { type: string; count: number }) => (
                <div key={s.type} className="flex items-center justify-between">
                  <span className="text-xs text-slate-300">{SIGNAL_LABELS[s.type] ?? s.type}</span>
                  <span className="text-sm font-medium text-white tabular-nums">{s.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detection Effectiveness */}
      <div className="bg-slate-800 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-white">Detection Effectiveness</h3>
          <span
            className="text-slate-500 cursor-help"
            title="Compares quality fail rates between flagged and unflagged members to measure detection lift."
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
        </div>
        {effectiveness.lift != null ? (
          <div className="flex items-center gap-4">
            <div>
              <p className="text-xs text-slate-400">Flagged QF rate</p>
              <p className="text-lg font-bold text-white tabular-nums">{(effectiveness.flaggedRate * 100).toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Unflagged QF rate</p>
              <p className="text-lg font-bold text-white tabular-nums">{(effectiveness.unflaggedRate * 100).toFixed(1)}%</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Lift</p>
              <p className="text-lg font-bold text-emerald-400 tabular-nums">{effectiveness.lift.toFixed(1)}x</p>
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-500">Insufficient data.</p>
        )}
      </div>
    </div>
  );
}

// ── Tab: Flagged Members ──────────────────────────────────────────────────

function FlaggedMembersTab() {
  const [page, setPage] = useState(1);
  const [tierFilter, setTierFilter] = useState<string | undefined>(undefined);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'fraud', 'flagged-members', page, tierFilter],
    queryFn: () => fetchFraudFlaggedMembers(page, 25, tierFilter),
  });

  const filterOptions = [
    { value: undefined, label: 'All' },
    { value: 'watch', label: 'Watch' },
    { value: 'shadow', label: 'Shadow' },
    { value: 'banned', label: 'Banned' },
  ] as const;

  return (
    <div className="space-y-4">
      {/* Tier filter */}
      <div className="flex gap-1">
        {filterOptions.map((opt) => (
          <button
            key={opt.label}
            onClick={() => { setTierFilter(opt.value); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              tierFilter === opt.value
                ? 'bg-slate-700 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-slate-400 text-sm">Loading...</p>
      ) : !data?.members?.length ? (
        <p className="text-slate-400 text-sm">No flagged members found.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-400 uppercase tracking-wide border-b border-slate-700">
                  <th className="text-left py-2 pr-4">Email</th>
                  <th className="text-left py-2 pr-4">Tier</th>
                  <th className="text-left py-2 pr-4">Score</th>
                  <th className="text-left py-2 pr-4">Top Signals</th>
                  <th className="text-left py-2 pr-4">Total</th>
                  <th className="text-left py-2 pr-4">Last Signal</th>
                  <th className="text-left py-2">Joined</th>
                </tr>
              </thead>
              <tbody>
                {data.members.map((m: {
                  id: string;
                  email: string;
                  fraudTier: string;
                  fraudScore: number;
                  topSignals: {
                    signal_type: string;
                    severity: string;
                    confidence: string;
                    created_at: string;
                  }[];
                  totalSignals: number;
                  lastSignalAt: string | null;
                  createdAt: string;
                }) => (
                  <tr key={m.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                    <td className="py-2.5 pr-4 text-white">{m.email}</td>
                    <td className="py-2.5 pr-4"><TierBadge tier={m.fraudTier} /></td>
                    <td className="py-2.5 pr-4 text-white tabular-nums">{m.fraudScore}</td>
                    <td className="py-2.5 pr-4">
                      <div className="flex flex-wrap gap-1">
                        {(m.topSignals ?? []).slice(0, 3).map((s, idx) => (
                          <span key={`${s.signal_type}-${idx}`} className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-slate-300">
                            {SIGNAL_LABELS[s.signal_type] ?? s.signal_type}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2.5 pr-4 text-white tabular-nums">{m.totalSignals}</td>
                    <td className="py-2.5 pr-4 text-slate-400 text-xs">{relativeTime(m.lastSignalAt)}</td>
                    <td className="py-2.5 text-slate-400 text-xs">{new Date(m.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">
              Page {data.page ?? page} of {data.totalPages ?? 1}
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800 text-slate-300 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                disabled={page >= (data.totalPages ?? 1)}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800 text-slate-300 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab: Signal Feed ──────────────────────────────────────────────────────

function SignalFeedTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'fraud', 'signal-feed'],
    queryFn: fetchFraudSignalFeed,
    refetchInterval: 30_000,
  });

  if (isLoading) return <p className="text-slate-400 text-sm">Loading signal feed...</p>;

  const signals: {
    id: string;
    createdAt: string;
    memberEmail: string;
    signalType: string;
    confidence: string;
    severity: string;
    details: string | null;
    aiNote: string | null;
  }[] = data?.signals ?? [];

  if (signals.length === 0) return <p className="text-slate-400 text-sm">No signals recorded yet.</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-slate-400 uppercase tracking-wide border-b border-slate-700">
            <th className="text-left py-2 pr-4">Time</th>
            <th className="text-left py-2 pr-4">Member</th>
            <th className="text-left py-2 pr-4">Signal Type</th>
            <th className="text-left py-2 pr-4">Confidence</th>
            <th className="text-left py-2 pr-4">Severity</th>
            <th className="text-left py-2 pr-4">Details</th>
            <th className="text-left py-2">AI Note</th>
          </tr>
        </thead>
        <tbody>
          {signals.map((s) => (
            <tr key={s.id} className="border-b border-slate-800 hover:bg-slate-800/50">
              <td className="py-2.5 pr-4 text-slate-400 text-xs whitespace-nowrap">{relativeTime(s.createdAt)}</td>
              <td className="py-2.5 pr-4 text-white text-xs">{s.memberEmail}</td>
              <td className="py-2.5 pr-4">
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-slate-300">
                  {SIGNAL_LABELS[s.signalType] ?? s.signalType}
                </span>
              </td>
              <td className="py-2.5 pr-4"><ConfidenceBadge confidence={s.confidence} /></td>
              <td className="py-2.5 pr-4"><SeverityBadge severity={s.severity} /></td>
              <td className="py-2.5 pr-4 text-slate-300 text-xs font-mono max-w-[200px] truncate">
                {s.details ? JSON.stringify(s.details).slice(0, 60) : '—'}
              </td>
              <td className="py-2.5 text-slate-400 text-xs max-w-[240px] truncate">
                {s.aiNote ? s.aiNote.slice(0, 80) : '\u2014'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Tab: Device Clusters ──────────────────────────────────────────────────

function DeviceClustersTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'fraud', 'device-clusters'],
    queryFn: fetchFraudDeviceClusters,
  });

  if (isLoading) return <p className="text-slate-400 text-sm">Loading device clusters...</p>;

  const clusters: {
    fingerprint: string;
    memberCount: number;
    members: { id: string; email: string; tier: string }[];
    firstSeen: string;
    lastSeen: string;
  }[] = data?.clusters ?? [];

  if (clusters.length === 0) return <p className="text-slate-400 text-sm">No device clusters detected.</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-slate-400 uppercase tracking-wide border-b border-slate-700">
            <th className="text-left py-2 pr-4">Fingerprint</th>
            <th className="text-left py-2 pr-4">Members</th>
            <th className="text-left py-2 pr-4">Member Emails</th>
            <th className="text-left py-2 pr-4">First Seen</th>
            <th className="text-left py-2">Last Seen</th>
          </tr>
        </thead>
        <tbody>
          {clusters.map((c) => {
            // Defensive null guards — the cluster endpoint occasionally
            // returns rows with `fingerprint` or `members` missing entirely
            // (mid-aggregation rows, fingerprints with zero matching
            // members after the join). Unguarded `.slice` / `.some` /
            // `.length` accesses crashed the entire /admin/fraud page when
            // any such row landed in the result set.
            const members = c.members ?? [];
            const fingerprint = c.fingerprint ?? '';
            const hasSuspicious = members.some((m) => m.tier === 'shadow' || m.tier === 'banned');
            return (
              <tr
                key={fingerprint || c.fingerprint}
                className={`border-b border-slate-800 ${hasSuspicious ? 'bg-orange-950/20' : 'hover:bg-slate-800/50'}`}
              >
                <td className="py-2.5 pr-4 text-white font-mono text-xs">{fingerprint.slice(0, 12)}</td>
                <td className="py-2.5 pr-4 text-white tabular-nums">{c.memberCount}</td>
                <td className="py-2.5 pr-4">
                  <div className="flex flex-wrap gap-1">
                    {members.slice(0, 3).map((m) => (
                      <span key={m.id} className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-slate-300">
                        {m.email}
                      </span>
                    ))}
                    {members.length > 3 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-slate-400">
                        +{members.length - 3} more
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-2.5 pr-4 text-slate-400 text-xs">{relativeTime(c.firstSeen)}</td>
                <td className="py-2.5 text-slate-400 text-xs">{relativeTime(c.lastSeen)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Tab: IP Clusters ──────────────────────────────────────────────────────

function IpClustersTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'fraud', 'ip-clusters'],
    queryFn: fetchFraudIpClusters,
  });

  if (isLoading) return <p className="text-slate-400 text-sm">Loading IP clusters...</p>;

  if (data?.note) {
    return (
      <div className="bg-slate-800 rounded-xl p-5">
        <p className="text-sm text-slate-300">{data.note}</p>
      </div>
    );
  }

  const clusters: {
    subnet: string;
    memberCount: number;
    members: { id: string; email: string }[];
  }[] = data?.clusters ?? [];

  if (clusters.length === 0) return <p className="text-slate-400 text-sm">No IP clusters detected.</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-slate-400 uppercase tracking-wide border-b border-slate-700">
            <th className="text-left py-2 pr-4">Subnet</th>
            <th className="text-left py-2 pr-4">Members</th>
            <th className="text-left py-2">Member Emails</th>
          </tr>
        </thead>
        <tbody>
          {clusters.map((c) => (
            <tr key={c.subnet} className="border-b border-slate-800 hover:bg-slate-800/50">
              <td className="py-2.5 pr-4 text-white font-mono text-xs">{c.subnet}</td>
              <td className="py-2.5 pr-4 text-white tabular-nums">{c.memberCount}</td>
              <td className="py-2.5">
                <div className="flex flex-wrap gap-1">
                  {c.members.map((m) => (
                    <span key={m.id} className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-slate-300">
                      {m.email}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Tab: Detection Effectiveness ──────────────────────────────────────────

function DetectionEffectivenessTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'fraud', 'overview'],
    queryFn: fetchFraudOverview,
  });

  if (isLoading) return <p className="text-slate-400 text-sm">Loading effectiveness data...</p>;

  const eff = data?.effectiveness;

  if (!eff || (eff.totalQualityFails != null && eff.totalQualityFails < 10)) {
    return (
      <div className="bg-slate-800 rounded-xl p-5">
        <p className="text-sm text-slate-300">Insufficient data — check back after more survey activity.</p>
      </div>
    );
  }

  const lift = eff.lift ?? 0;
  const flaggedRate = eff.flaggedRate ?? 0;
  const unflaggedRate = eff.unflaggedRate ?? 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800 rounded-xl p-5">
          <p className="text-xs text-slate-400 uppercase tracking-wide">Flagged Members — Quality Fail Rate</p>
          <p className="text-3xl font-bold text-white mt-2 tabular-nums">{(flaggedRate * 100).toFixed(1)}%</p>
          <p className="text-xs text-slate-500 mt-1">Members with at least one fraud signal</p>
        </div>
        <div className="bg-slate-800 rounded-xl p-5">
          <p className="text-xs text-slate-400 uppercase tracking-wide">Unflagged Members — Quality Fail Rate</p>
          <p className="text-3xl font-bold text-white mt-2 tabular-nums">{(unflaggedRate * 100).toFixed(1)}%</p>
          <p className="text-xs text-slate-500 mt-1">Members with no fraud signals</p>
        </div>
      </div>

      <div className="bg-slate-800 rounded-xl p-5">
        <p className="text-sm text-slate-300">
          Our system is{' '}
          <span className="text-emerald-400 font-bold">{lift.toFixed(1)}x</span>{' '}
          more likely to catch a fraudulent member than random chance.
        </p>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────

export default function FraudPanel() {
  const [activeTab, setActiveTab] = useState<Tab>('Overview');

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">Fraud Detection</h1>

      {/* Tab bar */}
      <div className="flex flex-wrap gap-1">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeTab === tab
                ? 'bg-slate-700 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="bg-slate-900 rounded-xl p-5">
        {activeTab === 'Overview' && <OverviewTab />}
        {activeTab === 'Flagged Members' && <FlaggedMembersTab />}
        {activeTab === 'Signal Feed' && <SignalFeedTab />}
        {activeTab === 'Device Clusters' && <DeviceClustersTab />}
        {activeTab === 'IP Clusters' && <IpClustersTab />}
        {activeTab === 'Detection Effectiveness' && <DetectionEffectivenessTab />}
      </div>
    </div>
  );
}
